import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Link2, Mail, MapPin, Merge, Pencil, Phone, Trash2, Unlink, User } from "lucide-react";
import api from "@/lib/api";
import { fmtEur, fmtMwhc } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STAGES, partnerName } from "./crmConst";
import { ActivityLog } from "./ActivityLog";
import { TaskList } from "./TaskList";
import { LinkProjectDialog } from "./LinkProjectDialog";
import { MergeDialog } from "./MergeDialog";

const Info = ({ icon: Icon, children }) => (children ? <div className="flex items-center gap-2 text-sm"><Icon className="h-3.5 w-3.5 text-muted-foreground" />{children}</div> : null);

const Projects = ({ projects, onLink, onUnlink }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-2">
      <Button size="sm" variant="outline" onClick={onLink} data-testid="crm-link-project"><Link2 className="h-4 w-4 mr-1" /> Rattacher un projet existant</Button>
      {!projects.length && <p className="text-sm text-muted-foreground">Aucun projet rattaché.</p>}
      {projects.map((p) => (
        <div key={p.id} className="flex items-stretch gap-1.5" data-testid={`crm-project-${p.id}`}>
          <button onClick={() => navigate(`/projects/${p.id}`)} className="flex-1 min-w-0 text-left rounded-lg border border-border px-3 py-2 hover:border-genis/60 transition-colors">
            <div className="flex justify-between gap-2">
              <span className="font-semibold text-sm truncate">{p.name}</span>
              <span className="metric text-sm font-bold text-genis">{fmtEur(p.result?.total_prime)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
              <span className="truncate">{[...new Set((p.operations || []).map((o) => o.fiche_code))].join(", ") || "—"} · {p.status}</span>
              <span>{fmtMwhc(p.result?.total_kwhc)}</span>
            </div>
          </button>
          <Button size="icon" variant="ghost" className="h-auto w-9 text-muted-foreground hover:text-red-600" title="Détacher du client" onClick={() => onUnlink(p)} data-testid={`crm-unlink-${p.id}`}>
            <Unlink className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};

export const ClientSheet = ({ clientId, refreshKey, partners, onClose, onEdit, onChanged }) => {
  const [c, setC] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const load = useCallback(() => clientId && api.get(`/crm/clients/${clientId}`).then((r) => setC(r.data)), [clientId]);
  useEffect(() => { setC(null); }, [clientId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const reload = () => { load(); onChanged(); };
  const setStage = async (stage) => { await api.patch(`/crm/clients/${clientId}/stage`, { stage }); reload(); };
  const del = async () => {
    if (!window.confirm(`Supprimer le client « ${c.company} » et son historique ? Les projets seront conservés.`)) return;
    await api.delete(`/crm/clients/${clientId}`); toast.success("Client supprimé"); onChanged(); onClose();
  };
  const unlink = async (p) => {
    if (!window.confirm(`Détacher le projet « ${p.name} » de ce client ?`)) return;
    await api.delete(`/crm/clients/${clientId}/projects/${p.id}`); toast.success("Projet détaché"); reload();
  };
  const total = (c?.projects || []).reduce((a, p) => a + (p.result?.total_prime || 0), 0);

  return (
    <Sheet open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="crm-client-sheet">
        {!c ? <div className="py-20 text-center text-muted-foreground">Chargement…</div> : (
          <div className="space-y-5">
            <SheetHeader>
              <SheetTitle className="text-xl font-heading text-left" data-testid="crm-sheet-title">{c.company}</SheetTitle>
              <p className="text-xs text-muted-foreground text-left">{partnerName(partners, c.partner_id)} · prime potentielle <b className="metric text-genis">{fmtEur(total)}</b></p>
            </SheetHeader>
            <div className="flex gap-2">
              <Select value={c.stage} onValueChange={setStage}>
                <SelectTrigger className="flex-1" data-testid="crm-sheet-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" onClick={() => onEdit(c)} data-testid="crm-sheet-edit"><Pencil className="h-4 w-4 mr-1" /> Modifier</Button>
              <Button variant="outline" title="Fusionner un doublon" onClick={() => setMergeOpen(true)} data-testid="crm-sheet-merge"><Merge className="h-4 w-4" /></Button>
              <Button variant="outline" className="text-red-600" onClick={del} data-testid="crm-sheet-delete"><Trash2 className="h-4 w-4" /></Button>
            </div>
            {[c.contact_name, c.email, c.phone, c.address, c.postal_code, c.city, c.comment].some(Boolean) && <div className="rounded-lg bg-secondary/50 p-3 space-y-1.5">
              <Info icon={User}>{c.contact_name}</Info>
              <Info icon={Mail}>{c.email && <a href={`mailto:${c.email}`} className="text-genis hover:underline">{c.email}</a>}</Info>
              <Info icon={Phone}>{c.phone && <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a>}</Info>
              <Info icon={MapPin}>{[c.address, c.postal_code, c.city].filter(Boolean).join(", ")}</Info>
              <Info icon={Building2}>{c.comment}</Info>
            </div>}
            <Tabs defaultValue="history">
              <TabsList className="w-full">
                <TabsTrigger value="history" className="flex-1" data-testid="crm-tab-history">Historique ({c.activities.length})</TabsTrigger>
                <TabsTrigger value="tasks" className="flex-1" data-testid="crm-tab-tasks">Tâches ({c.tasks.filter((t) => !t.done).length})</TabsTrigger>
                <TabsTrigger value="projects" className="flex-1" data-testid="crm-tab-projects">Projets ({c.projects.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="history" className="mt-4"><ActivityLog clientId={c.id} activities={c.activities} reload={reload} /></TabsContent>
              <TabsContent value="tasks" className="mt-4"><TaskList clientId={c.id} tasks={c.tasks} reload={reload} /></TabsContent>
              <TabsContent value="projects" className="mt-4"><Projects projects={c.projects} onLink={() => setLinkOpen(true)} onUnlink={unlink} /></TabsContent>
            </Tabs>
            <LinkProjectDialog open={linkOpen} onOpenChange={setLinkOpen} client={c} partners={partners} onLinked={reload} />
            <MergeDialog open={mergeOpen} onOpenChange={setMergeOpen} client={c} partners={partners} onMerged={() => { setMergeOpen(false); reload(); }} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
